import React, { useCallback } from 'react';
import styled from 'styled-components';

import useUI from 'src/hooks/use-ui';
import { ShareIcon } from 'shared/assets/share-icon';
import { VisuallyHidden } from 'shared/components/visually-hidden';

type ShareButtonProps = {
  height?: number | string;
  title?: string;
  width?: number | string;
};

export function ShareButton({ title, width = 24, height = 24 }: ShareButtonProps): JSX.Element | null {
  const { isEmbedded, iframeParentHref } = useUI();

  const handleShare = useCallback(async (): Promise<void> => {
    const sourceUrl = isEmbedded && iframeParentHref ? iframeParentHref : window.location.href;
    const currentUrl = typeof window !== 'undefined' ? sourceUrl : '';

    const shareData = {
      title,
      url: currentUrl,
    };

    try {
      await navigator.share(shareData);
    } catch (err) {
      console.error('Error sharing:', err);
    }
  }, [isEmbedded, iframeParentHref, title]);

  return (
    <ShareButtonContainer type='button' onClick={handleShare}>
      <VisuallyHidden>Share {title}</VisuallyHidden>
      <ShareIcon width={width} height={height} />
    </ShareButtonContainer>
  );
}

// The click handler lives on this <button> (not the inner ShareIcon svg) so the control is
// keyboard-activatable (Tab + Enter/Space fires a native button click) and so clicking
// anywhere in the button -- not just the icon glyph itself -- triggers the share action.
const ShareButtonContainer = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  position: relative;
  z-index: 1;
`;
